import * as bootstrap from 'bootstrap';

window.bootstrap = bootstrap;

const initBillingSwitcher = () => {
    const switcher = document.querySelector('[data-billing-switcher]');

    if (!switcher) {
        return;
    }

    const buttons = Array.from(switcher.querySelectorAll('[data-billing-cycle]'));
    const priceDisplays = document.querySelectorAll('[data-price-display]');
    const cycleDisplays = document.querySelectorAll('[data-cycle-display]');
    const priceCaptions = document.querySelectorAll('[data-price-caption]');
    const ctaButtons = document.querySelectorAll('[data-plan-cta]');

    const render = (cycle) => {
        buttons.forEach((button) => {
            button.classList.toggle('is-active', button.dataset.billingCycle === cycle);
        });

        priceDisplays.forEach((element) => {
            element.textContent = cycle === 'yearly' ? element.dataset.yearly : element.dataset.monthly;
        });

        cycleDisplays.forEach((element) => {
            element.textContent = cycle === 'yearly'
                ? element.dataset.yearlyLabel || '/ year'
                : element.dataset.monthlyLabel || '/ month';
        });

        priceCaptions.forEach((element) => {
            element.textContent = cycle === 'yearly'
                ? element.dataset.yearly || element.textContent
                : element.dataset.monthly || element.textContent;
        });

        ctaButtons.forEach((button) => {
            button.href = cycle === 'yearly' ? button.dataset.yearlyUrl : button.dataset.monthlyUrl;
        });
    };

    buttons.forEach((button) => {
        button.addEventListener('click', () => render(button.dataset.billingCycle));
    });
};

const initCycleSelector = () => {
    document.querySelectorAll('.public-cycle-selector').forEach((group) => {
        const options = Array.from(group.querySelectorAll('.public-cycle-selector__option'));

        const sync = () => {
            options.forEach((option) => {
                const input = option.querySelector('input[type="radio"]');
                option.classList.toggle('is-active', Boolean(input?.checked));
            });
        };

        options.forEach((option) => {
            option.addEventListener('click', () => {
                const input = option.querySelector('input[type="radio"]');

                if (!input) {
                    return;
                }

                input.checked = true;
                sync();
            });
        });

        sync();
    });
};

const initScrollReveal = () => {
    const elements = document.querySelectorAll('[data-reveal]');

    if (!elements.length) {
        return;
    }

    if (!('IntersectionObserver' in window)) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            const delay = entry.target.dataset.revealDelay || '0';
            entry.target.style.setProperty('--reveal-delay', `${delay}ms`);
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
    }, {
        threshold: 0.16,
        rootMargin: '0px 0px -8% 0px',
    });

    elements.forEach((element) => observer.observe(element));
};

const initPublicFormState = () => {
    document.querySelectorAll('[data-public-form]').forEach((form) => {
        form.addEventListener('submit', () => {
            const button = form.querySelector('button[type="submit"]');

            if (!button || button.disabled) {
                return;
            }

            const label = button.dataset.loadingLabel || button.textContent?.trim() || 'Processing';
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
            button.textContent = `${label}...`;
        }, { once: true });
    });
};

const initPublicWebchat = () => {
    const root = document.querySelector('[data-public-webchat]');

    if (!root) {
        return;
    }

    const launcher = root.querySelector('[data-webchat-launcher]');
    const panel = root.querySelector('[data-webchat-panel]');
    const closeButton = root.querySelector('[data-webchat-close]');
    const identityCard = root.querySelector('[data-webchat-identity-card]');
    const identityForm = root.querySelector('[data-webchat-identity-form]');
    const identityNameInput = root.querySelector('[data-webchat-name]');
    const identityPhoneInput = root.querySelector('[data-webchat-phone]');
    const identitySummary = root.querySelector('[data-webchat-identity-summary]');
    const identitySummaryName = root.querySelector('[data-webchat-identity-summary-name]');
    const identitySummaryPhone = root.querySelector('[data-webchat-identity-summary-phone]');
    const identityEditButton = root.querySelector('[data-webchat-identity-edit]');
    const identityDismissButton = root.querySelector('[data-webchat-identity-dismiss]');
    const identitySubmitButton = root.querySelector('[data-webchat-identity-submit]');
    const messages = root.querySelector('[data-webchat-messages]');
    const form = root.querySelector('[data-webchat-form]');
    const input = root.querySelector('[data-webchat-input]');
    const submit = root.querySelector('[data-webchat-submit]');
    const endpoint = root.dataset.endpoint;
    const sessionKey = root.dataset.sessionKey || 'orderfyi:webchat';
    const contactKey = root.dataset.contactKey || `${sessionKey}:contact`;
    const errorMessage = root.dataset.errorMessage || 'Nao consegui responder agora. Tente novamente.';
    const identityUnknownLabel = root.dataset.identityUnknown || 'Cliente identificado';
    const identityStartLabel = root.dataset.startIdentity || 'Iniciar';
    const identityEditLabel = root.dataset.editIdentity || 'Editar';
    const identitySaveLabel = root.dataset.saveIdentity || 'Salvar';
    const chooseProductLabel = root.dataset.chooseProduct || 'Escolher';
    const productFallbackLabel = root.dataset.productFallback || 'Produto';

    if (!launcher || !panel || !identityCard || !identityForm || !identityPhoneInput || !messages || !form || !input || !submit || !endpoint) {
        return;
    }

    let started = false;
    let pending = false;
    let identitySummaryDismissed = false;
    let editingIdentity = false;

    const generateSessionId = () => {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }

        return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const sessionId = (() => {
        const saved = window.localStorage.getItem(sessionKey);

        if (saved) {
            return saved;
        }

        const created = generateSessionId();
        window.localStorage.setItem(sessionKey, created);

        return created;
    })();

    const getIdentity = () => {
        try {
            const stored = window.localStorage.getItem(contactKey);
            const parsed = stored ? JSON.parse(stored) : null;

            if (!parsed || typeof parsed !== 'object') {
                return { name: '', phone: '' };
            }

            return {
                name: parsed.name || '',
                phone: parsed.phone || '',
            };
        } catch (error) {
            return { name: '', phone: '' };
        }
    };

    const saveIdentity = (identity) => {
        window.localStorage.setItem(contactKey, JSON.stringify(identity));
    };

    let contact = getIdentity();
    if (identityNameInput) {
        identityNameInput.value = contact.name;
    }
    identityPhoneInput.value = contact.phone;

    const scrollToBottom = () => {
        messages.scrollTop = messages.scrollHeight;
    };

    const removeActionRows = () => {
        messages.querySelectorAll('[data-webchat-actions]').forEach((element) => element.remove());
    };

    const appendMessage = (role, body) => {
        const article = document.createElement('article');
        const paragraph = document.createElement('p');

        article.className = `public-webchat__message public-webchat__message--${role}`;
        paragraph.textContent = body;
        article.appendChild(paragraph);

        messages.appendChild(article);
        scrollToBottom();
    };

    const appendProductCardsGrid = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        const article = document.createElement('article');
        const row = document.createElement('div');
        const rows = Math.ceil(items.length / 2);
        const cardHeightRem = 22;
        const rowGapRem = 0.5;
        const verticalPaddingRem = 1.7;

        article.className = 'public-webchat__message public-webchat__message--assistant public-webchat__message--cards';
        article.style.minHeight = `${(rows * cardHeightRem) + (Math.max(rows - 1, 0) * rowGapRem) + verticalPaddingRem}rem`;
        row.className = 'row g-2';

        items.forEach((item) => {
            const column = document.createElement('div');
            const card = document.createElement('div');
            const body = document.createElement('div');
            const index = document.createElement('small');
            const title = document.createElement('strong');
            const description = document.createElement('p');
            const footer = document.createElement('div');
            const price = document.createElement('span');
            const button = document.createElement('button');

            column.className = 'col-6';
            card.className = 'public-webchat__product-card';
            body.className = 'public-webchat__product-body';
            index.className = 'public-webchat__product-index';
            title.className = 'public-webchat__product-title';
            description.className = 'public-webchat__product-description';
            footer.className = 'public-webchat__product-footer';
            price.className = 'public-webchat__product-price';
            button.className = 'public-webchat__product-button';
            button.type = 'button';

            if (item?.image_url) {
                const media = document.createElement('div');
                const image = document.createElement('img');

                media.className = 'public-webchat__product-media';
                image.src = item.image_url;
                image.alt = item?.name || productFallbackLabel;
                media.appendChild(image);
                card.appendChild(media);
            }

            index.textContent = item?.position ? `#${item.position}` : '#';
            title.textContent = item?.name || productFallbackLabel;
            description.textContent = item?.short_description || item?.description || '';
            price.textContent = item?.price || '';
            button.textContent = chooseProductLabel;
            button.addEventListener('click', () => {
                void sendMessage(String(item?.position || ''), {
                    userLabel: `${chooseProductLabel} ${item?.name || productFallbackLabel}`,
                });
            });

            footer.append(price, button);
            body.append(index, title, description, footer);
            card.appendChild(body);
            column.appendChild(card);
            row.appendChild(column);
        });

        article.appendChild(row);
        messages.appendChild(article);
        scrollToBottom();
    };

    const buildActionButtons = (actions) => {
        if (!Array.isArray(actions) || actions.length === 0) {
            return null;
        }

        const wrapper = document.createElement('div');

        wrapper.className = 'public-webchat__actions';

        actions.forEach((action) => {
            if (!action?.label || !action?.value) {
                return;
            }

            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'public-webchat__action';
            button.textContent = action.label;
            button.addEventListener('click', () => {
                void sendMessage(action.value, { userLabel: action.label });
            });
            wrapper.appendChild(button);
        });

        return wrapper.childNodes.length > 0 ? wrapper : null;
    };

    const appendQuickActions = (actions) => {
        removeActionRows();

        if (!Array.isArray(actions) || actions.length === 0) {
            return;
        }

        const article = document.createElement('article');
        const wrapper = buildActionButtons(actions);

        if (!wrapper) {
            return;
        }

        article.className = 'public-webchat__message public-webchat__message--assistant public-webchat__message--actions';
        article.setAttribute('data-webchat-actions', 'true');

        article.appendChild(wrapper);
        messages.appendChild(article);
        scrollToBottom();
    };

    const renderHistory = (historyMessages, historyProducts = []) => {
        messages.innerHTML = '';

        historyMessages.forEach((message) => {
            if (!message?.body || !message?.role) {
                return;
            }

            appendMessage(message.role, message.body);
        });

        appendProductCardsGrid(historyProducts);
    };

    const setPending = (value) => {
        pending = value;
        input.disabled = value;
        submit.disabled = value;
        submit.setAttribute('aria-busy', value ? 'true' : 'false');
    };

    const parseResponseError = async (response) => {
        const contentType = response.headers.get('content-type') || '';

        if (!contentType.includes('application/json')) {
            return errorMessage;
        }

        const payload = await response.json().catch(() => null);

        if (!payload || typeof payload !== 'object') {
            return errorMessage;
        }

        if (typeof payload.message === 'string' && payload.message.trim()) {
            return payload.message.trim();
        }

        if (payload.errors && typeof payload.errors === 'object') {
            const firstError = Object.values(payload.errors)
                .flat()
                .find((value) => typeof value === 'string' && value.trim());

            if (firstError) {
                return firstError.trim();
            }
        }

        return errorMessage;
    };

    const syncIdentityVisibility = () => {
        const identified = Boolean(contact.phone);
        const contactName = contact.name || identityUnknownLabel;

        identityCard.hidden = identified && !editingIdentity;
        messages.hidden = !identified;
        form.hidden = !identified;

        if (identitySubmitButton) {
            identitySubmitButton.textContent = editingIdentity ? identitySaveLabel : identityStartLabel;
        }

        if (!identitySummary || !identitySummaryName || !identitySummaryPhone) {
            return;
        }

        identitySummaryName.textContent = contactName;
        identitySummaryPhone.textContent = contact.phone || '';
        identitySummary.hidden = !identified || identitySummaryDismissed || editingIdentity;
    };

    const sendMessage = async (body, options = {}) => {
        const text = body.trim();

        if (!text || pending) {
            return;
        }

        removeActionRows();

        if (!options.silentUser) {
            appendMessage('user', options.userLabel || text);
        }

        setPending(true);

        try {
            const response = await window.fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    message: text,
                    customer_name: contact.name || null,
                    customer_phone: contact.phone || null,
                }),
            });

            if (!response.ok) {
                throw new Error(await parseResponseError(response));
            }

            const data = await response.json();
            const responseMessages = Array.isArray(data.messages) ? data.messages : [];
            const responseProducts = Array.isArray(data.product_cards) ? data.product_cards : [];
            const responseActions = Array.isArray(data.quick_actions) ? data.quick_actions : [];

            responseMessages.forEach((message) => {
                if (message?.body) {
                    appendMessage('assistant', message.body);
                }
            });

            appendProductCardsGrid(responseProducts);
            appendQuickActions(responseActions);

            started = true;
        } catch (error) {
            appendMessage('assistant', error?.message || errorMessage);
        } finally {
            setPending(false);
            input.focus();
        }
    };

    const loadHistory = async () => {
        try {
            const url = new URL(endpoint, window.location.origin);
            url.searchParams.set('session_id', sessionId);

            if (contact.name) {
                url.searchParams.set('customer_name', contact.name);
            }

            if (contact.phone) {
                url.searchParams.set('customer_phone', contact.phone);
            }

            const response = await window.fetch(url, {
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('History request failed');
            }

            const data = await response.json();
            const historyMessages = Array.isArray(data.messages) ? data.messages : [];
            const historyProducts = Array.isArray(data.product_cards) ? data.product_cards : [];
            const historyActions = Array.isArray(data.quick_actions) ? data.quick_actions : [];

            renderHistory(historyMessages, historyProducts);
            appendQuickActions(historyActions);

            started = Boolean(data.found) && historyMessages.length > 0;

            return started;
        } catch (error) {
            return false;
        }
    };

    const openPanel = async () => {
        panel.hidden = false;
        root.classList.add('is-open');
        launcher.setAttribute('aria-expanded', 'true');

        if (!contact.phone) {
            syncIdentityVisibility();
            identityPhoneInput.focus();
            return;
        }

        syncIdentityVisibility();
        input.focus();

        const hasHistory = await loadHistory();

        if (!hasHistory) {
            void sendMessage('oi', { silentUser: true });
        }
    };

    const closePanel = () => {
        panel.hidden = true;
        root.classList.remove('is-open');
        launcher.setAttribute('aria-expanded', 'false');
    };

    launcher.addEventListener('click', () => {
        if (root.classList.contains('is-open')) {
            closePanel();
            return;
        }

        void openPanel();
    });

    closeButton?.addEventListener('click', closePanel);

    identityForm.addEventListener('submit', async (event) => {
        event.preventDefault();

        contact = {
            name: identityNameInput?.value.trim() || '',
            phone: identityPhoneInput.value.trim(),
        };

        if (!contact.phone) {
            identityPhoneInput.focus();
            return;
        }

        saveIdentity(contact);
        editingIdentity = false;
    identitySummaryDismissed = false;
        syncIdentityVisibility();
        input.focus();

        const hasHistory = await loadHistory();

        if (!hasHistory) {
            await sendMessage('oi', { silentUser: true });
        }
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();

        const text = input.value.trim();

        if (!text) {
            return;
        }

        input.value = '';
        void sendMessage(text);
    });

    identityDismissButton?.addEventListener('click', () => {
        identitySummaryDismissed = true;
        syncIdentityVisibility();
    });

    identityEditButton?.addEventListener('click', () => {
        editingIdentity = true;
        identitySummaryDismissed = false;

        if (identityNameInput) {
            identityNameInput.value = contact.name || '';
        }

        identityPhoneInput.value = contact.phone || '';
        syncIdentityVisibility();
        (identityNameInput || identityPhoneInput).focus();
    });

    syncIdentityVisibility();
};

const initNavbarScroll = () => {
    const navbar = document.querySelector('.public-navbar');
    if (!navbar) {
        return;
    }
    const handleScroll = () => {
        navbar.classList.toggle('is-scrolled', window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
};

initBillingSwitcher();
initCycleSelector();
initPublicFormState();
initScrollReveal();
initPublicWebchat();
initNavbarScroll();